<template>
  <div>
    <tool-bar :title="toolbarTitle"></tool-bar>

    <loading :loading="loading"></loading>

    <error-alert-item
      :error-alert-event="errorAlertEvent"
      :network-error="networkError"
      @resetErrorAlertEvent="errorAlertEvent=false"
    ></error-alert-item>

    <activity-redirect-item
      :loan-tasks="tasks"
      :current-loan-task="currentTaskName"
      :is-loan-task="true"
      :redirect-activity="redirectActivity"
    ></activity-redirect-item>

    <stage-comment-item></stage-comment-item>

    <refresh-item
      :show="showRefreshItem"
      @onRefresh="fetchInitialData"
    ></refresh-item>

    <div class="margin_top_20">
      <bank-statement-options-item
        :show="showBankStatementOptionsItem"
        :instructions="optionInstructions"
        :options="bankStatementOptions"
        @onBankStatementOptionSelected="onBankStatementOptionSelected"
      ></bank-statement-options-item>

      <bank-statement-manual-upload-item
        ref="bankStatementUploadItem"
        :tasks="tasks"
        :show="showBankStatementManualUploadItem"
        :instruction="uploadInstruction"
        :allow-only-p-d-f="allowOnlyPDF"
        @onBankStatementsUploaded="completeCurrentTaskAndGetNextTask"
      ></bank-statement-manual-upload-item>
    </div>
  </div>
</template>

<script>
import ToolBar from "../../layout/ToolBar";
import Loading from "../../layout/Loading";
import ErrorAlertItem from "../../layout/ErrorAlertItem";
import StageCommentItem from "../../layout/StageCommentItem";
import ActivityRedirectItem from "../../layout/ActivityRedirectItem";
import BankStatementOptionsItem from "../../layout/BankStatementOptionsItem";
import BankStatementManualUploadItem from "../../layout/BankStatementManualUploadItem";
import DataSource from "../../../data/DataSource";
import LoanScopeDataHolder from "../../../data/model/LoanScopeDataHolder";
import { EntityType } from "../../../utils/EntityType";
import { Activities } from "../../../utils/ActivityNavigator";
import ActivityNavigator from "../../../utils/ActivityNavigator";
import AttributeUtil, { AttributeDetails } from "../../../utils/AttributeUtil";
import { LoanStageConfigKeys } from "../../../utils/LoanStageConfigKeys";
import RefreshItem from "../../layout/RefreshItem";
import LoanTasksActivityNavigator from "../../../utils/LoanTasksActivityNavigator";
import { BankStatementOptions } from "../../../utils/BankStatementOptions";
import storageUtil from "../../../utils/StorageUtil";
import { Constants } from "@/components/Constants";

export default {
  name: "BankStatementActivity",
  components: {
    ActivityRedirectItem,
    ToolBar,
    BankStatementOptionsItem,
    BankStatementManualUploadItem,
    Loading,
    ErrorAlertItem,
    StageCommentItem,
    RefreshItem
  },

  data: () => ({
    toolbarTitle: "Bank Statement",
    optionInstructions: [],
    bankStatementOptions: [],
    pdfUploadInstructions: "Upload PDF of the Bank Statement for the last 3 months.",
    pdfOrImageUploadInstructions: "Upload PDF or Images of the Bank Statement for the last 3 months.",
    showRefreshItem: false,
    showBankStatementOptionsItem: false,
    showBankStatementManualUploadItem: false,
    allowOnlyPDF: false,
    loading: false,
    errorAlertEvent: false,
    networkError: {}
  }),

  methods: {
    showError(error) {
      this.loading = false;
      this.networkError = error;
      this.errorAlertEvent = true;
    },

    onBankStatementOptionSelected(selectedOption) {
      const attributeDto = AttributeUtil.getAttributeUpdateDto(
        EntityType.LOANS,
        LoanScopeDataHolder.getLoanId(),
        AttributeUtil.getSingleAttributeData(
          AttributeDetails.BANK_STATEMENT_TYPE.key,
          AttributeDetails.BANK_STATEMENT_TYPE.name,
          selectedOption
        )
      );

      this.loading = true;
      DataSource.createOrUpdateAttribute(attributeDto)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data)
          } else {
            if (selectedOption === BankStatementOptions.NET_BANKING.value) {
              LoanScopeDataHolder.setPerfiosTransactionType(Constants.NET_BANKING);
              ActivityNavigator.navigateTo(Activities.PERFIOS);
            } else if (selectedOption === BankStatementOptions.PERFIOS_PDF.value) {
              LoanScopeDataHolder.setPerfiosTransactionType(Constants.UPLOAD_STATEMENT);
              ActivityNavigator.navigateTo(Activities.PERFIOS);
            } else {
              this.allowOnlyPDF = (BankStatementOptions.BANK_GENERATED_PDF.value === selectedOption);
              this.showBankStatementOptionsItem = false;
              this.showBankStatementManualUploadItem = true;
              this.$refs.bankStatementUploadItem.fetchInitialData();
            }
          }
        })
    },

    completeCurrentTaskAndGetNextTask() {
      this.loading = true;
      LoanTasksActivityNavigator.getNextTask(this.tasks)
        .then(res => {
          this.loading = false;
          if (res.errorOccurred) {
            this.showError(res.data);
          }
        });
    },

    fetchInitialData() {
      this.showRefreshItem = false;
      this.loading = true;
      DataSource.getLoanStageConfigData(LoanScopeDataHolder.getLoanId(), this.currentTaskName)
        .then(stageConfigRes => {
          this.loading = false;
          if (stageConfigRes.errorOccurred) {
            this.showError(stageConfigRes.data);
            this.showRefreshItem = true;
          } else {
            this.showBankStatementOptionsItem = true;
            const stageConfigData = stageConfigRes.data ? stageConfigRes.data.properties : null;
            if (stageConfigData) {
              const bankStatementStageData = stageConfigData[LoanStageConfigKeys.BANK_STATEMENT_INSTRUCTIONS];
              if (bankStatementStageData) {
                this.optionInstructions = bankStatementStageData.optionInstructions;
                if (bankStatementStageData.pdfOrImageUploadInstructions) {
                  this.pdfOrImageUploadInstructions = bankStatementStageData.pdfOrImageUploadInstructions;
                }
                if (bankStatementStageData.pdfUploadInstructions) {
                  this.pdfUploadInstructions = bankStatementStageData.pdfUploadInstructions;
                }
                if (bankStatementStageData.netBankingEnabled) {
                  this.bankStatementOptions.push(BankStatementOptions.NET_BANKING);
                }
                if (bankStatementStageData.perfiosPdfEnabled) {
                  this.bankStatementOptions.push(BankStatementOptions.PERFIOS_PDF);
                }
                if (bankStatementStageData.pdfParsingEnabled) {
                  this.bankStatementOptions.push(BankStatementOptions.BANK_GENERATED_PDF);
                }
                if (bankStatementStageData.imageFromGalleryEnabled) {
                  this.bankStatementOptions.push(BankStatementOptions.GALLERY_UPLOAD);
                }
              }
            }
          }
        });
    }
  },

  created: function () {
    this.fetchInitialData();
  },

  computed: {
    redirectActivity() {
      return Activities.LOAN_DETAILS;
    },

    tasks() {
      return [Activities.BANK_STATEMENT];
    },

    currentTaskName() {
      return LoanScopeDataHolder.getCurrentLoanTask()
        ? LoanScopeDataHolder.getCurrentLoanTask().name
        : "";
    },

    uploadInstruction() {
      return this.allowOnlyPDF ? this.pdfUploadInstructions : this.pdfOrImageUploadInstructions;
    },
  }
}
</script>

<style scoped src="../../../assets/style/base.css"></style>
